import { ReactNode } from 'react';

import { RadioGroupItem } from '@tg-web/components';
import { PropsWithCN, cn } from '@tg-web/utils';

export type IslandRadioCellProps = PropsWithCN & {
  children: ReactNode;
  /**
   * Doubles as the control's id, which is what the row's `htmlFor` points at,
   * so the values of two radio groups on one page have to stay distinct.
   */
  value: string;
};

/**
 * A 56px row with its radio in the trailing slot, where the whole row selects.
 *
 * Built like `IslandCheckboxCell` and for the same reasons: the row is the
 * `<label>` itself rather than a div wrapping one, so the gutters are part of
 * the tap target, and label activation does not re-forward a click that started
 * inside the control — one tap is one selection wherever it lands.
 * `cursor-pointer` is what gets iOS to apply `:active` to a non-button.
 *
 * There is no leading slot. These rows are a list of choices rather than
 * settings, so an empty 26px icon column would only indent the text.
 */
export function IslandRadioCell({
  children,
  className,
  value,
}: IslandRadioCellProps) {
  return (
    <label
      className={cn(
        'flex h-14 w-full cursor-pointer items-center pl-4 pr-[14px] transition-colors active:bg-black/[0.06]',
        className,
      )}
      htmlFor={value}
    >
      <span className="typo-text text-tg-text min-w-0 flex-1 truncate">
        {children}
      </span>
      {/* The same 32px trailing slot an IslandCell gives its right icon. */}
      <div className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center">
        <RadioGroupItem id={value} value={value} />
      </div>
    </label>
  );
}
